/**
 * remember-me — the SINGLE source of truth for session lifetime.
 *
 * The Supabase client persists the session in localStorage (auto-generated;
 * we don't edit it). To honour an unchecked "Remember me" we mark the sign-in
 * as ephemeral, and clear the persisted session only when we can tell the
 * browser session genuinely ended.
 *
 *   - Remember me ON  (default): session persists across browser restarts.
 *   - Remember me OFF: session lasts for the current browser session only.
 *
 * Rules that must not be broken:
 *   - `enforceEphemeralSessionOnBoot()` runs ONCE at module load, before any
 *     component or hook reads the stored session. A session must never be
 *     cleared after the membership/auth check has already read it.
 *   - Ambiguity always favours staying signed in. A new tab, a reload, or a
 *     restored tab group must never sign a remembered member out.
 */

const EPHEMERAL_KEY = "rt-auth-ephemeral";
const TAB_ALIVE_KEY = "rt-auth-tab-alive";
/** Heartbeat so a second tab can tell the browser session is still running. */
const SEEN_KEY = "rt-auth-ephemeral-seen";
const SEEN_WINDOW_MS = 120_000;

let enforced = false;
let heartbeat: number | undefined;

function touchSeen() {
  try {
    localStorage.setItem(SEEN_KEY, String(Date.now()));
  } catch {
    /* storage unavailable */
  }
}

function startHeartbeat() {
  if (typeof window === "undefined" || heartbeat !== undefined) return;
  touchSeen();
  heartbeat = window.setInterval(touchSeen, 30_000);
  window.addEventListener("focus", touchSeen);
  window.addEventListener("beforeunload", touchSeen);
}

/** Call immediately after a successful sign-in. */
export function markSessionEphemeral(remember: boolean) {
  if (typeof window === "undefined") return;
  try {
    if (remember) {
      localStorage.removeItem(EPHEMERAL_KEY);
      localStorage.removeItem(SEEN_KEY);
      sessionStorage.removeItem(TAB_ALIVE_KEY);
    } else {
      localStorage.setItem(EPHEMERAL_KEY, "1");
      // This tab is alive, so reloads here never wipe the session.
      sessionStorage.setItem(TAB_ALIVE_KEY, "1");
      startHeartbeat();
    }
  } catch {
    /* storage unavailable */
  }
}

/** Forget every remember-me marker (used on password reset / sign-out). */
export function clearRememberMe() {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(EPHEMERAL_KEY);
    localStorage.removeItem(SEEN_KEY);
    sessionStorage.removeItem(TAB_ALIVE_KEY);
  } catch {
    /* storage unavailable */
  }
}

/**
 * Runs once at module load (see src/routes/__root.tsx). If the previous
 * sign-in was ephemeral and the browser session has genuinely ended, the
 * stored auth tokens are cleared. In every ambiguous case the session is
 * kept — "not sure" must never sign a member out.
 */
export function enforceEphemeralSessionOnBoot() {
  if (typeof window === "undefined" || enforced) return;
  enforced = true;
  try {
    const ephemeral = localStorage.getItem(EPHEMERAL_KEY) === "1";
    if (!ephemeral) return;

    const tabAlive = sessionStorage.getItem(TAB_ALIVE_KEY) === "1";
    const seen = Number(localStorage.getItem(SEEN_KEY) ?? 0);
    const browserStillOpen = Number.isFinite(seen)
      && seen > 0
      && Date.now() - seen < SEEN_WINDOW_MS;

    if (tabAlive || browserStillOpen) {
      // Same browser session (this tab, or another tab still running).
      sessionStorage.setItem(TAB_ALIVE_KEY, "1");
      startHeartbeat();
      return;
    }

    // Fresh browser session after an ephemeral sign-in → clear auth tokens.
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith("sb-") && key.endsWith("-auth-token")) {
        localStorage.removeItem(key);
      }
    }
    localStorage.removeItem(EPHEMERAL_KEY);
    localStorage.removeItem(SEEN_KEY);
  } catch {
    /* storage unavailable */
  }
}
